<template>
  <Teleport to="body">
    <Modal :show="showForm" title="Assign Container" size="md" @close="handleClose">
      <form @submit.prevent class="space-y-5">
        <!-- Container Type Selection -->
        <div>
          <label for="ac-container-type" class="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
            Container Type <span class="text-red-500" aria-hidden="true">*</span><span class="sr-only">(required)</span>
          </label>
          <select
            id="ac-container-type"
            v-model="selectedContainer"
            class="w-full px-4 py-2.5 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
          >
            <option :value="null" disabled>Select a container type…</option>
            <option
              :value="container.id"
              v-for="container in documents"
              :key="container.id"
            >
              {{ container.containerType }}
            </option>
          </select>
        </div>

        <!-- Max Volume Display -->
        <div v-if="selectedContainer">
          <label class="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
            Max Volume
          </label>
          <div class="px-4 py-3 bg-neutral-50 dark:bg-neutral-900 rounded-lg border border-neutral-200 dark:border-neutral-700">
            <p class="text-lg font-semibold text-neutral-800 dark:text-neutral-100">{{ maxVolume * quantity }} L</p>
          </div>
        </div>

        <!-- Quantity Controls -->
        <div v-if="selectedContainer">
          <label class="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
            Quantity
          </label>
          <div class="flex items-center justify-between px-4 py-3 bg-neutral-50 dark:bg-neutral-900 rounded-lg border border-neutral-200 dark:border-neutral-700">
            <p class="text-xl font-semibold text-neutral-800 dark:text-neutral-100">{{ quantity }}</p>
            <div class="flex items-center gap-2">
              <button
                @click="quantity -= 1"
                type="button"
                aria-label="Decrease quantity"
                class="w-11 h-11 flex items-center justify-center text-neutral-700 dark:text-neutral-200 bg-neutral-100 dark:bg-neutral-700 border border-neutral-300 dark:border-neutral-600 rounded-full hover:bg-neutral-200 dark:hover:bg-neutral-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                :disabled="quantity <= 0"
              >
                <span class="text-2xl leading-none" aria-hidden="true">-</span>
              </button>
              <button
                @click="quantity += 1"
                type="button"
                aria-label="Increase quantity"
                class="w-11 h-11 flex items-center justify-center text-neutral-700 dark:text-neutral-200 bg-neutral-100 dark:bg-neutral-700 border border-neutral-300 dark:border-neutral-600 rounded-full hover:bg-neutral-200 dark:hover:bg-neutral-600 transition-colors"
              >
                <span class="text-2xl leading-none" aria-hidden="true">+</span>
              </button>
            </div>
          </div>
        </div>

        <!-- Error Message -->
        <div v-if="errorMessage" role="alert" class="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/40 rounded-lg">
          <p class="text-sm text-red-700 dark:text-red-300">{{ errorMessage }}</p>
        </div>
      </form>

      <template #footer>
        <div class="flex items-center justify-end gap-3">
          <button
            @click="handleClose"
            :disabled="isPending"
            type="button"
            class="px-5 py-2.5 text-sm font-medium text-neutral-700 dark:text-neutral-300 bg-white dark:bg-neutral-700 border border-neutral-300 dark:border-neutral-600 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-600 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            @click="handleClick"
            :disabled="isPending || !selectedContainer || quantity < 1"
            type="button"
            class="px-5 py-2.5 text-sm font-medium text-white bg-primary-700 rounded-lg hover:bg-primary-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <svg
              v-if="isPending"
              class="animate-spin h-4 w-4"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span>Add</span>
          </button>
        </div>
      </template>
    </Modal>
  </Teleport>
</template>

<script>
import getCollection from '@/composables/getCollection'
import useToast from '@/composables/useToast'
import Modal from '@/components/ui/Modal.vue'
import { db } from '@/firebase/config'
import { doc, onSnapshot, updateDoc } from '@firebase/firestore'
import { ref, watch, watchEffect } from 'vue'

export default {
    components: { Modal },
    props: [ 'showForm', 'supplierId', 'locationId' ],
    emits: [ 'close' ],
    setup(props, context) {
        const { showToast } = useToast()
        const quantity = ref(0)
        const selectedContainer = ref(null)
        const selectedContainerObj = ref(null)
        const maxVolume = ref(0)
        const isPending = ref(false)
        const errorMessage = ref('')
        const containers = ref([])

        const { documents } = getCollection('containers')

        let docRef = doc(db, 'suppliers', props.supplierId)

        let unsub = onSnapshot(docRef, (doc) => {
            if (doc.data()) {
                if (doc.data().containers) {
                    containers.value = doc.data().containers
                }
            }
        })

        watchEffect((onInvalidate) => {
            onInvalidate(() => unsub())
        })

        watch(selectedContainer, () => {
            documents.value.forEach(container => {
                if (container.id == selectedContainer.value) {
                    maxVolume.value = container.maxVolume
                    selectedContainerObj.value = container
                    quantity.value = 0
                }
            });
        })

        const handleClick = async () => {
            // Guarded by the disabled state too, but never trust the DOM alone.
            if (!selectedContainerObj.value || quantity.value < 1) {
                errorMessage.value = 'Select a container type and a quantity of at least 1.'
                return
            }
            isPending.value = true
            errorMessage.value = ''
            try {
                const containerData = {
                    'locationId': props.locationId,
                    'containerType': selectedContainerObj.value.containerType,
                    'containerTypeId': selectedContainerObj.value.id,
                    'maxVolume': selectedContainerObj.value.maxVolume,
                    'quantity': quantity.value
                }
                // Build a new array instead of mutating the snapshot-backed one —
                // a failed save must not leave a phantom entry behind for retries.
                const data = { 'containers': [...containers.value, containerData] }

                let supplierRef = doc(db, 'suppliers', props.supplierId)
                await updateDoc(supplierRef, data, {merge: true})

                showToast('success', 'Container assigned')
                closeModal()
            } catch (error) {
                console.error('Error assigning container:', error)
                errorMessage.value = 'Failed to assign the container. Please try again.'
            } finally {
                isPending.value = false
            }
        }

        // Close modal
        const closeModal = () => {
            context.emit('close')
        }

        // Ignore close requests (Cancel, X, Escape) while the save is in flight.
        const handleClose = () => {
            if (isPending.value) return
            closeModal()
        }

        return { closeModal, handleClose, documents, quantity, selectedContainer, maxVolume, handleClick, isPending, errorMessage }
    }
}
</script>
